import {ReactNode, useCallback} from 'react';
import './page.scss';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useNavigate } from 'react-router-dom';
import FormInput from '@/components/form-components/form-input.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { buttonVariants, containerVariants } from '@/constants/animations.ts';
import commonService from '@/services/common.service.ts';
import { EMAIL_SENT_ROUTE, LOGIN_ROUTE } from '@/constants/RoutesConfig.tsx';
import { BackIcon } from '@/constants/ImageConfig.ts';
import { ForgotPasswordValidationSchema } from '@/services/validations/auth.validation.ts';

interface ForgotPasswordValues {
    email: string;
}

const initialValues: ForgotPasswordValues = {
    email: '',
};

const ForgotPasswordScreen = () => {
    const navigate = useNavigate();

    const onSubmit = useCallback(
        async (
            values: ForgotPasswordValues,
            { setSubmitting, setErrors }: FormikHelpers<ForgotPasswordValues>
        ) => {
            try {
                setSubmitting(true);
                await commonService._account.sendResetPasswordEmail(values);
                commonService._alert.showToast('Reset password email sent successfully', 'success');
                navigate(`${EMAIL_SENT_ROUTE}?email=${values.email}`);
            } catch (error: any) {
                setErrors({ email: error?.message });
                commonService._alert.showToast(error?.detail || 'Something went wrong', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [navigate]
    );

    return (
        <motion.div
            className="forgot-password-component"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div className="auth-screen-title-wrapper" variants={containerVariants}>
                <div className="auth-screen-title">Reset your password</div>
                <div className="auth-screen-subtitle text-secondary-foreground mt-2 mb-4">
                    Enter your email address and we'll send you a link to reset your password.
                </div>
            </motion.div>

            <Formik
                initialValues={initialValues}
                validationSchema={ForgotPasswordValidationSchema}
                onSubmit={onSubmit}
                validateOnChange
                validateOnBlur={false}
                enableReinitialize
                validateOnMount
            >
                {({ isSubmitting }) => (
                    <Form noValidate={true}>
                        <Field name="email">
                            {(field: any) => (
                                <FormInput
                                    name="email"
                                    label="Email Address"
                                    placeholder=""
                                    icon={<Mail className="size-4 text-gray-500 peer-focus:text-blue-500"/>}
                                    formikField={field}
                                />
                            )}
                        </Field>

                        <motion.div variants={buttonVariants} whileHover="hover" className="mt-6">
                            <Button 
                                fullWidth 
                                loading={isSubmitting}
                                mixpanelEvent={{
                                    name: "forgot_password_send_reset_link_clicked",
                                    properties: {}
                                }}
                            >
                                Send reset link
                            </Button>
                        </motion.div>

                        <motion.div variants={buttonVariants} whileHover="hover">
                            <Button
                                fullWidth
                                type="button"
                                variant="link"
                                onClick={() => navigate(LOGIN_ROUTE)}
                                prefixIcon={<BackIcon />}
                                mixpanelEvent={{
                                    name: "forgot_password_back_to_signin_clicked",
                                    properties: {}
                                }}
                            >
                                Back to Sign In
                            </Button>
                        </motion.div>
                    </Form>
                ) as ReactNode}
            </Formik>
        </motion.div>
    );
};

export default ForgotPasswordScreen;
